<!--
  ATTENTION

  This component is used when using the old layout (supportsNewLayout FF is off).
  The new layout period picker is implemented in the:

  - app/javascript/src/application/layout/components/period-picker-sidebar-trigger.vue

  So be cautious when changing this component, as it might require changes on the above file also!
-->

<template>
  <a
    class="dropdown-toggle-periodpicker dropdown-left"
    data-walkthrough-id="dropdown-toggle-periodpicker"
    :href="url"
    @click.prevent.stop="handleToggleDropdown"
  >
    <span class="fa" :class="iconClass"></span>
  </a>
  <div
    class="
      dropdown-menu dropdown-menu-periodpicker
      period-picker-trigger__drawer
    "
    data-behaviour="periodpicker set-current-ledger"
    :data-period="period"
  >
    <PeriodPicker
      v-if="shouldShowDropdown"
      :current-ledger-id="currentLedgerId ?? undefined"
      :ledgers-per-year="ledgersPerYear"
      :years="years"
      @change-compare-period="triggerNavigation"
      @close-period-picker="handleToggleDropdown"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import PeriodPicker from './period-picker.vue'
import { usePeriodPicker } from '../stores/period-picker'
import { buildPickerTriggerUrl } from '../utils'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { dropdowns } from '@app/shared/types/dropdown'

defineOptions({
  name: 'PeriodPickerTrigger',
})

interface Props {
  period: `${number}${number}/${number}${number}${number}${number}`
  setCurrentLedger: boolean
  url: string
}

const props = defineProps<Props>()

const dropdownStore = useDropdown()

const { currentLedgerId, hasRequiredData, isLoading, ledgersPerYear, years } =
  storeToRefs(usePeriodPicker())

const { requestData } = usePeriodPicker()

const drawerDisplay = computed(() => {
  return shouldShowDropdown.value ? 'block' : 'none'
})

const iconClass = computed(() => {
  return {
    'fa-spin fa-spinner': isLoading.value,
    'fa-chevron-down': !isLoading.value && !shouldShowDropdown.value,
    'fa-chevron-up': !isLoading.value && shouldShowDropdown.value,
  }
})

const shouldShowDropdown = computed(
  () => isScopedDropdownOpen.value && hasRequiredData.value
)

const isScopedDropdownOpen = computed(
  () => dropdownStore.currentDropdownId === dropdowns.PERIOD_PICKER
)

const mappingScreenRegexp = /\/accounts\/(table_)?edit_mapping/

function handleToggleDropdown() {
  if (isLoading.value) {
    return
  }

  if (!isScopedDropdownOpen.value && !hasRequiredData.value) {
    requestData(props.url)
  }

  dropdownStore.toggleDropdown(dropdowns.PERIOD_PICKER)
}

function triggerNavigation(ledgerId: string) {
  let newUrl = window.location.href.replace(
    /\/ledgers\/[0-9]+/,
    `/ledgers/${ledgerId}`
  )

  /*
    When the mapping can be skipped, we want navigation to go directly to
    the workflows page, even if we are currently on the mapping screen.
    If the mapping on the ledger we are navigating to will be required,
    the controller will redirect us to the mapping page anyway.
  */
  if (mappingScreenRegexp.test(newUrl)) {
    newUrl = newUrl.replace(mappingScreenRegexp, '/workflows')
  }

  window.Turbolinks.visit(
    props.setCurrentLedger
      ? buildPickerTriggerUrl(newUrl, 'current_ledger_id', ledgerId)
      : newUrl
  )
}
</script>

<style lang="scss" scoped>
.period-picker-trigger {
  &__drawer {
    display: v-bind(drawerDisplay);
  }
}
</style>
